import { FC, useState, useEffect, useRef } from 'react'
import { TextField, TextFieldProps, InputAdornment, Typography } from '@mui/material'

export interface MoneyInputProps extends Omit<TextFieldProps, 'value' | 'onChange' | 'type'> {
  value?: number // Price in cents
  onChange: (cents: number) => void
}

export const MoneyInput: FC<MoneyInputProps> = ({
  value,
  onChange,
  onBlur,
  ...textFieldProps
}) => {
  const [displayValue, setDisplayValue] = useState<string>('')
  const [isFocused, setIsFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Convert cents to dollars for display
  const centsToDollars = (cents: number | undefined): string => {
    if (cents === undefined || cents === null || isNaN(cents)) {
      return ''
    }
    return (cents / 100).toFixed(2)
  }

  // Convert dollars to cents
  const dollarsToCents = (dollars: string): number => {
    const num = parseFloat(dollars)
    if (isNaN(num)) {
      return 0
    }
    return Math.round(num * 100)
  }

  // Update display value when value prop changes (only if not focused, to avoid interrupting typing)
  useEffect(() => {
    if (!isFocused) {
      setDisplayValue(centsToDollars(value))
    }
  }, [value, isFocused])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value
    // Only update local display value, don't call onChange yet
    setDisplayValue(inputValue)
  }

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true)
    if (textFieldProps.onFocus) {
      textFieldProps.onFocus(event)
    }
  }

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false)
    
    // Format to 2 decimal places on blur
    const cents = dollarsToCents(displayValue)
    const formatted = centsToDollars(cents)
    setDisplayValue(formatted)
    
    // Call onChange with the formatted value (even if it's 0)
    onChange(cents)
    
    // Call original onBlur if provided
    if (onBlur) {
      onBlur(event)
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      // Format and save on Enter, similar to blur
      const cents = dollarsToCents(displayValue)
      const formatted = centsToDollars(cents)
      setDisplayValue(formatted)
      onChange(cents)
      
      // Blur the input to close editing
      if (inputRef.current) {
        inputRef.current.blur()
      }
    } else if (event.key === 'Escape') {
      // Reset to original value on Escape
      setDisplayValue(centsToDollars(value))
      if (inputRef.current) {
        inputRef.current.blur()
      }
    }
    
    // Call original onKeyDown if provided
    if (textFieldProps.onKeyDown) {
      textFieldProps.onKeyDown(event)
    }
  }

  return (
    <TextField
      {...textFieldProps}
      ref={inputRef}
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      type="text"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Typography>$</Typography>
          </InputAdornment>
        ),
        ...textFieldProps.InputProps,
      }}
      placeholder="0.00"
    />
  )
}

